/**
 * A month against the one before it: the totals in, out and set aside, and the
 * biggest category of each, every figure with how it changed.
 *
 * Pure. It is handed the database's sums per month, direction and category —
 * `[{ month: 'YYYY-MM', kind: 'income' | 'spent' | 'saved', key, sum }]`, every
 * `sum` positive — and says nothing in words: a change is a direction and a
 * percentage, and the display translates them.
 */

import { round2 } from './money.js'

export const COMPARED = ['income', 'spent', 'saved']

/**
 * How `now` differs from `before`:
 *   up / down  with `percent`, the change as a whole number of per cent
 *   same       no change worth a per cent
 *   new        nothing before, something now — there is no percentage of zero
 *   none       nothing either time
 */
export function change(now, before) {
  const diff = round2(now - before)
  if (before <= 0) return { direction: now > 0 ? 'new' : 'none', diff, percent: null }
  const percent = Math.round((Math.abs(diff) / before) * 100)
  if (percent === 0) return { direction: 'same', diff, percent: 0 }
  return { direction: diff > 0 ? 'up' : 'down', diff, percent }
}

/**
 * @param {object[]} groups   the sums, as above
 * @param {string} month      'YYYY-MM', the month being looked at
 * @param {string} previous   'YYYY-MM', the one it is compared with
 * @returns {{ totals: object[], tops: object[] }} one entry per kind in each,
 *   in `COMPARED` order. A top is `null`-keyed when the month has nothing of
 *   that kind; otherwise it is compared with *the same category* the month
 *   before, whether or not that was the biggest then.
 */
export function compareMonths(groups, month, previous) {
  const sums = (m, kind) => {
    const byKey = new Map()
    for (const g of groups ?? []) {
      if (g.month !== m || g.kind !== kind) continue
      byKey.set(g.key ?? '', round2((byKey.get(g.key ?? '') ?? 0) + (Number(g.sum) || 0)))
    }
    return byKey
  }
  const total = (byKey) => round2([...byKey.values()].reduce((a, b) => a + b, 0))

  const totals = []
  const tops = []
  for (const kind of COMPARED) {
    const now = sums(month, kind)
    const before = sums(previous, kind)
    const a = total(now)
    const b = total(before)
    totals.push({ kind, now: a, before: b, ...change(a, b) })

    // The biggest this month; a tie goes to the name first in the alphabet, so
    // the card does not flicker between two equal categories.
    let top = null
    for (const [key, sum] of now) {
      if (sum <= 0) continue
      if (!top || sum > top.sum || (sum === top.sum && key < top.key)) top = { key, sum }
    }
    if (!top) {
      tops.push({ kind, key: null, now: 0, before: 0, direction: 'none', diff: 0, percent: null })
      continue
    }
    const was = before.get(top.key) ?? 0
    tops.push({ kind, key: top.key, now: top.sum, before: was, ...change(top.sum, was) })
  }
  return { totals, tops }
}

/** A change for the worse smaller than this many per cent is a warning, not an alarm. */
export const SMALL_CHANGE = 10

/**
 * Whether a change is good news, as a tone. More coming in and more set aside
 * is good; more going out is bad. A bad change under `SMALL_CHANGE` per cent
 * is `warn`, a bigger one `danger`; a good one is `good` whatever its size.
 * No change, or nothing to measure it against, is `neutral`.
 */
export function trendTone(kind, figure) {
  if (figure.direction !== 'up' && figure.direction !== 'down') return 'neutral'
  const good = kind === 'spent' ? figure.direction === 'down' : figure.direction === 'up'
  if (good) return 'good'
  return figure.percent < SMALL_CHANGE ? 'warn' : 'danger'
}
